import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, KeyRound, Send, LogOut } from "lucide-react";

// Paso obligatorio antes de poder usar la tienda/panel: sin esto no hay
// forma de recuperar una cuenta si se olvida la contraseña (el "correo" de
// registro es falso, @clientes.local). Se muestra en vez de cualquier ruta
// protegida mientras el usuario no tenga real_email -- ver Protected en App.js.
export default function LinkEmailGate() {
  const { user, refresh, logout } = useAuth();
  const { tenant } = useTenant();
  const color = tenant?.primary_color || "#DC2626";
  const [step, setStep] = useState("request"); // "request" | "confirm"
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error("Escribe un correo");
    setBusy(true);
    try {
      await api.post("/account/link-email/request", { email: email.trim() });
      toast.success("Te mandamos un código a ese correo");
      setStep("confirm");
    } catch (ex) { toast.error(apiError(ex.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) return toast.error("Escribe el código");
    setBusy(true);
    try {
      await api.post("/account/link-email/confirm", { code: code.trim() });
      toast.success("¡Correo vinculado!");
      await refresh();
    } catch (ex) { toast.error(apiError(ex.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm animate-fade-up">
        <div className="rounded-2xl overflow-hidden shadow-md">
          <div className="px-6 py-5 text-white flex items-center gap-2.5" style={{ background: color }}>
            <Mail size={22} />
            <h1 className="font-head text-lg font-extrabold">Vincula tu correo</h1>
          </div>
          <div className="bg-white p-6">
            {step === "request" ? (
              <>
                <p className="text-sm text-slate-600 mb-5">
                  Hola {user?.name} -- necesitamos tu correo para que puedas <b>recuperar tu cuenta</b> si
                  olvidas la contraseña. Es un paso único, no volverás a verlo.
                </p>
                <form onSubmit={requestCode} className="space-y-4">
                  <label className="block">
                    <span className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5 block">Tu correo</span>
                    <input data-testid="gate-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@correo.com" autoFocus required
                      className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors" />
                  </label>
                  <button data-testid="gate-send-code" disabled={busy}
                    className="w-full flex items-center justify-center gap-2 text-white font-bold rounded-lg py-3.5 transition-opacity hover:opacity-90 disabled:opacity-60"
                    style={{ background: color }}>
                    <Send size={16} /> {busy ? "Enviando..." : "Enviarme el código"}
                  </button>
                </form>
              </>
            ) : (
              <>
                <p className="text-sm text-slate-600 mb-5">Escribe el código de 6 dígitos que te llegó a <b>{email}</b>.</p>
                <form onSubmit={confirmCode} className="space-y-4">
                  <input data-testid="gate-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" inputMode="numeric" maxLength={6} autoFocus required
                    className="w-full text-center tracking-[0.5em] font-mono text-lg border border-slate-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors" />
                  <button data-testid="gate-confirm" disabled={busy}
                    className="w-full flex items-center justify-center gap-2 text-white font-bold rounded-lg py-3.5 transition-opacity hover:opacity-90 disabled:opacity-60"
                    style={{ background: color }}>
                    <KeyRound size={16} /> {busy ? "Confirmando..." : "Confirmar"}
                  </button>
                  <button type="button" onClick={() => setStep("request")} className="w-full text-xs text-slate-400 hover:underline">Usar otro correo</button>
                </form>
              </>
            )}
          </div>
        </div>
        <button onClick={logout} className="mt-4 mx-auto flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors">
          <LogOut size={13} /> Cerrar sesión
        </button>
      </div>
    </div>
  );
}
